import type { MessageResponse } from '@/domain/Api';
import type { Ingredient } from '@/domain/Ingredient';
import type { Supplement } from '@/domain/Supplement';
import { api } from '@/lib/api';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';

export interface IngredientsResponse extends MessageResponse {
  data: Record<string, Array<Ingredient>>;
}

export interface IngredientResponse extends MessageResponse {
  data: Ingredient;
}

export const ingredientsQuery = () => {
  return queryOptions({
    queryKey: queryKeys.ingredients.all(),
    queryFn: async () => api.get('ingredients').json<IngredientsResponse>(),
  });
};

export const ingredientQuery = (ingredientId: number) => {
  return queryOptions({
    queryKey: queryKeys.ingredients.ingredient(ingredientId),
    queryFn: async () =>
      api.get(`ingredients/${ingredientId}`).json<IngredientResponse>(),
    enabled: ingredientId > 0,
  });
};

export interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
  page: number | null;
}

export interface SupplementsResponse {
  data: Array<Supplement>;
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: Array<PaginationLink>;
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export const getAdminIngredientsQuery = (page = 1) =>
  queryOptions({
    queryKey: queryKeys.adminIngredients.all(page),
    queryFn: async () => {
      const response = await api
        .get('admin/ingredients', { searchParams: { page } })
        .json<SupplementsResponse>();
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getAllAdminIngredientsQuery = () =>
  queryOptions({
    queryKey: queryKeys.adminIngredients.all('all'),
    queryFn: async () => {
      const response = await api
        .get('admin/ingredients', { searchParams: { per_page: 500 } })
        .json<SupplementsResponse>();
      return response;
    },
  });

export interface IngredientDetailResponse {
  data: Supplement;
}

export const getAdminIngredientQuery = (id: number) =>
  queryOptions({
    queryKey: queryKeys.adminIngredients.single(id),
    queryFn: async () => {
      const response = await api
        .get(`admin/ingredients/${id}`)
        .json<IngredientDetailResponse>();
      return response.data;
    },
    enabled: id > 0,
  });

export interface IngredientCategoriesResponse extends MessageResponse {
  data: Array<string>;
}

/** Fallback when the categories endpoint is unavailable */
export const FALLBACK_INGREDIENT_CATEGORIES = [
  'NAD⁺ precursors & polyphenols',
  'Herbs & botanicals',
  'Mushroom extracts',
  'Plant compounds',
  'Vitamins & minerals',
] as const;

export const getAdminIngredientCategoriesQuery = () =>
  queryOptions({
    queryKey: queryKeys.adminIngredients.categories(),
    queryFn: async () => {
      const response = await api
        .get('admin/ingredients/categories')
        .json<IngredientCategoriesResponse | Array<string>>();

      if (Array.isArray(response)) {
        return response;
      }

      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }

      return [...FALLBACK_INGREDIENT_CATEGORIES];
    },
    staleTime: 5 * 60 * 1000,
  });
